import type { CalculatorFormData, CalculatorResult, StepConfig } from './calculatorTypes';
import type { CalcTexts } from './locales';
import flow from './calculatorFlow.json';

export const steps: StepConfig[] = flow.steps as StepConfig[];

export function getStepById(id: string): StepConfig | undefined {
  return steps.find(s => s.id === id);
}

export function resolveNextStep(step: StepConfig, formData: CalculatorFormData): string {
  const { next } = step;
  if (typeof next === 'string') return next;
  if ('childrenOneOrTwo' in next) {
    const total =
      formData.children_0_5 + formData.children_6_13 +
      formData.children_14_17 + formData.children_18_24_non_working;
    return (total === 1 || total === 2) ? next.childrenOneOrTwo.true : next.childrenOneOrTwo.false;
  }
  const branch = next as { true: string; false: string };
  const conditionValue = formData[step.saveKey as keyof CalculatorFormData];
  return conditionValue ? branch.true : branch.false;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getText(texts: any, keyPath: string): string {
  const parts = keyPath.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current: any = texts;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return keyPath;
    current = current[part];
  }
  return typeof current === 'string' ? current : keyPath;
}

export function kids(n: number, texts: CalcTexts): string {
  return (n === 1 ? texts.common.children.singular : texts.common.children.plural)
    .replace('{count}', String(n));
}

export function formatEuroValue(n: number): string {
  return n.toLocaleString('de-DE') + ' €';
}

export function formatCalculatorReport(result: CalculatorResult): string {
  const { grundsicherung, bedarf, totalAnrechenbares, formData } = result;
  const {
    shared_household, pregnant,
    children_0_5, children_6_13, children_14_17, children_18_24_non_working,
    checkbox_income_user, income_user_gross, income_user_net,
    checkbox_income_partner, income_partner_gross, income_partner_net,
    checkbox_other_income, rental_income, unemployment_benefit, pension, other_income_amount,
    checkbox_housing_costs, cold_rent, additional_costs, heating_costs,
  } = formData;

  const toNum = (v: string) => { const n = parseInt(v, 10); return isNaN(n) ? 0 : n; };
  const euro = formatEuroValue;

  const lines: string[] = [
    `Berechnetes Grundsicherungsgeld pro Monat: ${euro(grundsicherung)}`,
    `Monatlicher Bedarf: ${euro(bedarf)}`,
    `Anzurechnendes Einkommen: ${euro(totalAnrechenbares)}`,
  ];

  if (shared_household) lines.push('Sie leben mit einer Partnerin / einem Partner in einer gemeinsamen Wohnung.');

  const childrenTotal = children_0_5 + children_6_13 + children_14_17 + children_18_24_non_working;
  if (childrenTotal > 0) {
    if (children_0_5 > 0) lines.push(`${children_0_5} Kind/Kinder (0–5 Jahre)`);
    if (children_6_13 > 0) lines.push(`${children_6_13} Kind/Kinder (6–13 Jahre)`);
    if (children_14_17 > 0) lines.push(`${children_14_17} Kind/Kinder (14–17 Jahre)`);
    if (children_18_24_non_working > 0) lines.push(`${children_18_24_non_working} Kind/Kinder (18–24 Jahre)`);
  }

  if (pregnant) {
    lines.push(shared_household
      ? 'Sie oder Ihre Partnerin sind schwanger (ab der 13. Woche)'
      : 'Sie sind schwanger (ab der 13. Woche)');
  }

  if (!checkbox_income_user && toNum(income_user_gross) > 0) lines.push(`Ihr Bruttoeinkommen: ${euro(toNum(income_user_gross))}`);
  if (!checkbox_income_user && toNum(income_user_net) > 0) lines.push(`Ihr Nettoeinkommen: ${euro(toNum(income_user_net))}`);
  if (shared_household && !checkbox_income_partner && toNum(income_partner_gross) > 0) lines.push(`Bruttoeinkommen Ihrer Partnerin / Ihres Partners: ${euro(toNum(income_partner_gross))}`);
  if (shared_household && !checkbox_income_partner && toNum(income_partner_net) > 0) lines.push(`Nettoeinkommen Ihrer Partnerin / Ihres Partners: ${euro(toNum(income_partner_net))}`);
  if (!checkbox_other_income && toNum(rental_income) > 0) lines.push(`Mieteinnahmen: ${euro(toNum(rental_income))}`);
  if (!checkbox_other_income && toNum(unemployment_benefit) > 0) lines.push(`Arbeitslosengeld I: ${euro(toNum(unemployment_benefit))}`);
  if (!checkbox_other_income && toNum(pension) > 0) lines.push(`Rente: ${euro(toNum(pension))}`);
  if (!checkbox_other_income && toNum(other_income_amount) > 0) lines.push(`Sonstiges Einkommen: ${euro(toNum(other_income_amount))}`);
  if (!checkbox_housing_costs && toNum(cold_rent) > 0) lines.push(`Kaltmiete: ${euro(toNum(cold_rent))}`);
  if (!checkbox_housing_costs && toNum(additional_costs) > 0) lines.push(`Nebenkosten (ohne Heizung und Strom): ${euro(toNum(additional_costs))}`);
  if (!checkbox_housing_costs && toNum(heating_costs) > 0) lines.push(`Heizkosten: ${euro(toNum(heating_costs))}`);

  return lines.join(', ');
}
